import {
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import { motion } from "framer-motion";
import { BotContext } from "@/pages/cbb-builder/store";
import {
  LinkRejectedIcon,
  ReSyncingFailedIcon,
  ReSyncingIcon,
  TrainingCompletedIcon,
  TrainingProgressIcon,
} from "@/pages/cbb-builder/components/AiBotAssets";
import formatNumber from "@/hooks/UseFormatNumber";
import { WithoutBgDeleteIcon } from "@/assets/AiBotAssets";
import { axiosPost } from "@/services/axiosService";
import { userSlice } from "@/redux/User";
import RemoveConfirmation from "@/pages/cbb-builder/components/RemoveConfirmation";
import ModalWrapper from "@/pages/cbb-builder/components/ModalWrapper";

const statusIcons: Record<string, ReactNode> = {
  "Training Complete": <TrainingCompletedIcon />,
  "In Progress": <TrainingProgressIcon />,
  "Link Rejected": <LinkRejectedIcon />,
  "Limit Reached": <LinkRejectedIcon />,
  "Re-syncing": <ReSyncingIcon />,
  "Re-sync Complete": <TrainingCompletedIcon />,
  "Re-sync Failed": <ReSyncingFailedIcon />,
};

const GlobeIcon = () => {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M7.99992 14.6668C11.6818 14.6668 14.6666 11.6821 14.6666 8.00016C14.6666 4.31826 11.6818 1.3335 7.99992 1.3335C4.31802 1.3335 1.33325 4.31826 1.33325 8.00016C1.33325 11.6821 4.31802 14.6668 7.99992 14.6668Z"
        stroke="#6498EA"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M5.33324 2H5.9999C4.6999 5.89333 4.6999 10.1067 5.9999 14H5.33324"
        stroke="#6498EA"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10 2C11.3 5.89333 11.3 10.1067 10 14"
        stroke="#6498EA"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M2 10.6667V10C5.89333 11.3 10.1067 11.3 14 10V10.6667"
        stroke="#6498EA"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M2 5.9999C5.89333 4.6999 10.1067 4.6999 14 5.9999"
        stroke="#6498EA"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const tableGridClass =
  "grid  grid-cols-[minmax(260px,1fr)_160px_112px_86px] items-center";
const TRAINING_DELETE_STATUSES = new Set(["In Progress", "Re-syncing"]);

function buildLinkDeleteMessage(urlsToDelete: string[], urls: any[]) {
  const pendingDeleteSet = new Set(urlsToDelete);
  const deletingTrainingItems = urls.filter(
    (item: any) =>
      pendingDeleteSet.has(item.url) &&
      TRAINING_DELETE_STATUSES.has(item.status ?? ""),
  );

  if (deletingTrainingItems.length > 0) {
    return `${urlsToDelete.length > 1 ? "Some selected links are" : "This link is"} still training. Deleting ${urlsToDelete.length > 1 ? `these ${urlsToDelete.length} links` : "this link"} will stop training. This action cannot be undone.`;
  }

  return `Are you sure you want to delete ${urlsToDelete.length > 1 ? `${urlsToDelete.length} trained links` : "this trained link"}? This action cannot be undone.`;
}

const OnboardingTrainedLinksTable = () => {
  const [store, storeDispatch] = useContext(BotContext);
  const dispatch = useDispatch();
  const [selectedUrls, setSelectedUrls] = useState<string[]>([]);
  const [pendingDeleteUrls, setPendingDeleteUrls] = useState<string[]>([]);
  const [removeData, setRemoveData] = useState<{
    status: boolean;
    remove: () => void;
  }>({
    status: false,
    remove: () => {},
  });
  const [deleteLoading, setDeleteLoading] = useState(false);

  const urls = useMemo(
    () => store.botData?.aiSource?.urls ?? [],
    [store.botData?.aiSource?.urls],
  );
  const selectableUrls = useMemo(() => urls.map((d: any) => d.url), [urls]);

  const hasAnySelectable = useMemo(
    () => selectableUrls.length > 0,
    [selectableUrls],
  );
  const allSelectableChecked =
    hasAnySelectable &&
    selectableUrls.every((url) => selectedUrls.includes(url));
  const selectedCount = selectedUrls.length;

  useEffect(() => {
    setSelectedUrls((prev) =>
      prev.filter((url) => selectableUrls.includes(url)),
    );
  }, [selectableUrls]);

  const updateUrls = (nextUrls: any[], characterUsed?: number) => {
    storeDispatch({
      type: "set-aiSource",
      dataKey: "urls",
      dataSet: nextUrls,
      characterUsed,
    });
  };

  const handleToggleSelectAll = () => {
    if (!hasAnySelectable) return;
    setSelectedUrls(allSelectableChecked ? [] : selectableUrls);
  };

  const handleToggleUrl = (url: string) => {
    setSelectedUrls((prev) =>
      prev.includes(url) ? prev.filter((item) => item !== url) : [...prev, url],
    );
  };

  const handleDelete = (url: string) => {
    const urlsToDelete = [url];
    setPendingDeleteUrls(urlsToDelete);
    setRemoveData({
      status: true,
      remove: () => handleConfirmDelete(urlsToDelete),
    });
  };

  const handleBulkDelete = () => {
    if (selectedCount === 0) return;
    const urlsToDelete = [...selectedUrls];
    setPendingDeleteUrls(urlsToDelete);
    setRemoveData({
      status: true,
      remove: () => handleConfirmDelete(urlsToDelete),
    });
  };

  const handleConfirmDelete = async (urlsToDelete: string[]) => {
    try {
      if (!urlsToDelete.length) return;
      setDeleteLoading(true);

      const sources = urls
        .filter((item: any) => urlsToDelete.includes(item.url))
        .map((item: any) => ({
          ...item,
          characterCount: item.characterCount ?? item.characters ?? 0,
          url: item.url ?? item.link,
        }));

      const res = await axiosPost("/ai/delete-content", {
        sources,
        botCustomId: store.botData.botCustomId,
        botCreatedAt: store.botData.createdAt,
        sourceType: "urls",
      });

      const pendingDeleteSet = new Set(urlsToDelete);
      const nextUrls = urls.filter(
        (item: any) => !pendingDeleteSet.has(item.url),
      );

      updateUrls(nextUrls, res.data.characterUsed);
      setSelectedUrls((prev) =>
        prev.filter((item) => !pendingDeleteSet.has(item)),
      );

      dispatch(
        userSlice.actions.setToastPopUp({
          show: true,
          toastType: "success",
          message:
            "Selected data has been successfully removed from the training data.",
        }),
      );
      setPendingDeleteUrls([]);
      setRemoveData((prev) => ({ ...prev, status: false }));
    } catch (err: any) {
      dispatch(
        userSlice.actions.setToastPopUp({
          show: true,
          toastType: "error",
          message:
            err?.response?.data?.message ||
            err?.message ||
            "Failed to delete links",
        }),
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-[12px]">
      <div className="flex items-center justify-between gap-[14px]">
        <h3 className="text-[14px] font-semibold leading-[110%] text-[#444]">
          Trained Links{" "}
          <span
            style={{ color: "#666", fontSize: "14px", fontWeight: "normal" }}
          >
            ({urls?.length ?? 0})
          </span>
        </h3>

        {selectedCount >= 1 && (
          <motion.button
            className="flex h-[30px] items-center justify-center gap-[5px] rounded-[8px] bg-[#FFF7F7] px-[8px] text-[#fb5f66] transition-colors"
            type="button"
            whileTap={{ scale: 0.985 }}
            onClick={handleBulkDelete}
          >
            <WithoutBgDeleteIcon />
            <span className="mt-[2px]">
              {allSelectableChecked ? "Delete All" : "Delete"}
            </span>
          </motion.button>
        )}
      </div>

      <div className="overflow-hidden rounded-[16px] border border-[rgba(0,0,0,0.10)] bg-white">
        <div className="new-scrollbar overflow-x-auto overflow-y-hidden">
          <div className="w-full min-w-[660px]">
            <div
              className={`${tableGridClass} min-h-[46px] border-b border-[rgba(0,0,0,0.08)] bg-[#fafafa] text-[13px] font-semibold leading-[110%] text-[#444] ${urls.length > 6 ? "pe-[5px]" : ""}`}
            >
              <div className="flex h-full items-center gap-[14px] px-[20px]">
                <input
                  aria-label="Select all trained links"
                  className="onboarding-custom-checkbox"
                  type="checkbox"
                  checked={allSelectableChecked}
                  disabled={!hasAnySelectable}
                  onChange={handleToggleSelectAll}
                />
                <span className="font-medium leading-[110%] text-[#444]">
                  Source
                </span>
              </div>
              <div className="flex h-full items-center justify-start border-l border-[rgba(0,0,0,0.08)] px-[20px] font-medium leading-[110%] text-[#444]">
                Status
              </div>
              <div className="flex h-full items-center justify-start border-l border-[rgba(0,0,0,0.08)] px-[20px] font-medium leading-[110%] text-[#444]">
                Characters
              </div>
              <div className="flex h-full items-center justify-start border-l border-[rgba(0,0,0,0.08)] px-[20px] font-medium leading-[110%] text-[#444]">
                Action
              </div>
            </div>

            <div className="new-scrollbar max-h-[400px] overflow-y-auto overflow-x-hidden">
              {!urls.length ? (
                <div className="flex min-h-[120px] items-center justify-center text-[14px] leading-[140%] text-[#777]">
                  No trained links yet.
                </div>
              ) : (
                urls.map((data: any, index: number) => {
                  const status = data.status ?? "In Progress";

                  return (
                    <div
                      className={`${tableGridClass} min-h-[54px] border-b border-[rgba(0,0,0,0.08)] text-[13px] leading-[130%] text-[#444] last:border-b-0`}
                      key={data.url ?? index}
                    >
                      <div className="flex min-w-0 items-center gap-[16px] px-[20px]">
                        <input
                          aria-label={`Select ${data.title ?? data.url}`}
                          className="onboarding-custom-checkbox"
                          type="checkbox"
                          checked={selectedUrls.includes(data.url)}
                          onChange={() => handleToggleUrl(data.url)}
                        />
                        <div className="flex w-[calc(100%_-_26px)] items-center gap-[5px]">
                          <span className="shrink-0">
                            <GlobeIcon />
                          </span>
                          <span
                            className="min-w-0 truncate text-[13px] leading-[110%] text-[#444]"
                            title={data.title ?? data.url}
                          >
                            {data.title ?? data.url}
                          </span>
                        </div>
                      </div>
                      <div
                        className={`flex h-full items-center gap-[7px] whitespace-nowrap border-l border-[rgba(0,0,0,0.08)] px-[20px] ${
                          status === "In Progress" ? "rotating-svg" : ""
                        } ${status === "Re-syncing" ? "resyncing-svg" : ""}`}
                      >
                        {statusIcons[status]}
                        <span className="truncate text-[13px] leading-[110%] text-[#444]">
                          {status}
                        </span>
                      </div>
                      <div className="flex h-full items-center border-l border-[rgba(0,0,0,0.08)] px-[20px] text-[#555]">
                        {formatNumber(data.characterCount ?? 0)}
                      </div>
                      <div className="relative flex h-full items-center justify-center border-l border-[rgba(0,0,0,0.08)] px-[20px]">
                        <button
                          type="button"
                          title="Delete source"
                          aria-label="Delete source"
                          className="flex h-[32px] w-[32px] items-center justify-center rounded-[8px] text-[#fb5f66] transition-colors hover:bg-[#FFF7F7]"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleDelete(data.url);
                          }}
                        >
                          <WithoutBgDeleteIcon />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>
      {removeData.status && pendingDeleteUrls.length > 0 && (
        <ModalWrapper>
          <RemoveConfirmation
            removeData={removeData}
            setRemoveData={setRemoveData}
            isLoading={deleteLoading}
            title="Delete Trained Link"
            message={buildLinkDeleteMessage(pendingDeleteUrls, urls)}
          />
        </ModalWrapper>
      )}
    </div>
  );
};

export default OnboardingTrainedLinksTable;
